import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { CreditCard, Grid3x3, List, Table, Settings } from "lucide-react"
import { SchemaPropertySelector } from "./schema-property-selector"

interface PropertiesPanelProps {
  element: any
  dataSchema: any
  onElementUpdate?: (element: any) => void
}

export function PropertiesPanel({ element, dataSchema, onElementUpdate }: PropertiesPanelProps) {
  const [showScopeSelector, setShowScopeSelector] = useState(false)

  const getIconForType = (type: string) => {
    switch (type) {
      case "card":
        return <CreditCard className="h-4 w-4" />
      case "card_array":
        return <Grid3x3 className="h-4 w-4" />
      case "table":
        return <Table className="h-4 w-4" />
      case "list":
        return <List className="h-4 w-4" />
      default:
        return <CreditCard className="h-4 w-4" />
    }
  }

  if (!element) {
    return (
      <div className="w-[320px] border-l border-border bg-background flex flex-col">
        <div className="border-b border-border px-4 py-3 bg-muted/30">
          <h3 className="font-semibold text-sm">Properties</h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div>
            <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
              <Settings className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">Click an element to view its properties</p>
          </div>
        </div>
      </div>
    )
  }

  const updateProperty = (key: string, value: any) => {
    const updatedElement = {
      ...element,
      [key]: value,
    }
    onElementUpdate?.(updatedElement)
  }

  return (
    <div className="w-[320px] border-l border-border bg-background flex flex-col">
      <div className="border-b border-border px-4 py-3 bg-muted/30">
        <h3 className="font-semibold text-sm">Properties</h3>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Element Type */}
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Element Type</Label>
            <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-md">
              <span className="text-muted-foreground">{getIconForType(element.type)}</span>
              <span className="text-sm font-medium capitalize">{element.type.replace("_", " ")}</span>
            </div>
          </div>

          {/* Header (all types) */}
          <div className="space-y-2">
            <Label htmlFor="header">Header</Label>
            <Input
              id="header"
              value={element.header || ""}
              onChange={(e) => updateProperty("header", e.target.value)}
              placeholder="Enter header text"
            />
          </div>

          {/* Scope (all types) */}
          <div className="space-y-2">
            <Label htmlFor="scope">Scope (Data Binding)</Label>
            <div className="flex gap-2">
              <Input
                id="scope"
                value={element.scope || ""}
                onChange={(e) => updateProperty("scope", e.target.value)}
                placeholder="e.g., user.profile"
                className="flex-1"
              />
              <Button size="sm" variant="outline" onClick={() => setShowScopeSelector(true)} className="shrink-0">
                <Settings className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Path to data scope for this element</p>
          </div>

          {/* With Key (Sub-scope) */}
          <div className="space-y-2">
            <Label htmlFor="with_key">With Key (Sub-scope)</Label>
            <Input
              id="with_key"
              value={element.with_key || ""}
              onChange={(e) => updateProperty("with_key", e.target.value)}
              placeholder="e.g., properties"
            />
            <p className="text-xs text-muted-foreground">
              Selects a key within the current scope to use as the sub-scope for children
            </p>
          </div>

          {/* Key (for table and list) */}
          {(element.type === "table" || element.type === "list") && (
            <div className="space-y-2">
              <Label htmlFor="key">Key</Label>
              <Input
                id="key"
                value={element.key || ""}
                onChange={(e) => updateProperty("key", e.target.value)}
                placeholder="Enter key"
              />
            </div>
          )}

          {/* Index (for card_array) */}
          {element.type === "card_array" && (
            <div className="space-y-2">
              <Label htmlFor="index">Index Variable</Label>
              <Input
                id="index"
                value={element.index || ""}
                onChange={(e) => updateProperty("index", e.target.value)}
                placeholder="e.g., item"
              />
              <p className="text-xs text-muted-foreground">Variable name for array iteration</p>
            </div>
          )}

          {/* Keys (for card and card_array) */}
          {(element.type === "card" || element.type === "card_array") && (
            <div className="space-y-2">
              <Label htmlFor="keys">Keys (JSON)</Label>
              <Textarea
                id="keys"
                value={JSON.stringify(element.keys || [], null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value)
                    updateProperty("keys", parsed)
                  } catch {
                    // Invalid JSON, ignore
                  }
                }}
                placeholder='["field1", "field2"] or [["field1", "field2"], ["field3"]]'
                rows={4}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">Array of keys or nested arrays for layout</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* SchemaPropertySelector dialog */}
      <SchemaPropertySelector
        open={showScopeSelector}
        onOpenChange={setShowScopeSelector}
        dataSchema={JSON.stringify(dataSchema)}
        onSelect={(scope) => {
          updateProperty("scope", scope)
          setShowScopeSelector(false)
        }}
      />
    </div>
  )
}
